"use client"

import {
  NavigateWithThread,
  EntityDetailSkeleton,
  MatchDetailShell,
  EvaluationAnalysisView,
  PageContainer,
  PageHeader,
  PageHeaderActions,
  PageHeaderLeading,
  ProfilePdfDownloadButton,
  isProfilePdfEnabled,
  PersonProfileStandingView,
  useRetryFitAnalysis,
} from '@yeshuman/sdk'
import { ApplicationCoverLetterView } from './application-cover-letter-view'
import { AnalysisRetryButton } from '../search/analysis-retry-button'
import { useJobWorkspacePersonPageState } from '../../hooks/use-job-workspace-person-page-state'

export function JobWorkspacePersonPage() {
  const state = useJobWorkspacePersonPageState()
  const { retryEmployer, mutatingApplicationId } = useRetryFitAnalysis()

  if (state.missingRequired) {
    return <NavigateWithThread to="/employer/jobs" />
  }

  if (state.isLoading) {
    return <EntityDetailSkeleton variant="job" ariaLabel="Loading person" />
  }

  if (state.missingData || !state.profile || !state.opportunity) {
    return (
      <PageContainer>
        <PageHeaderLeading
          breadcrumbs={[
            { label: 'Jobs', to: '/employer/jobs' },
            { label: 'Job' },
          ]}
        />
        <PageHeader title="Person" description="This person could not be loaded." />
      </PageContainer>
    )
  }

  const { profile } = state
  const analysisRetry =
    state.applicant && state.jobId ? (
      <AnalysisRetryButton
        mode={state.hasAnalysis ? 'run' : 'try'}
        onClick={() => retryEmployer(state.applicant!.id, state.jobId)}
        pending={mutatingApplicationId === state.applicant.id}
      />
    ) : null

  const profilePanel = (
    <PersonProfileStandingView
      profile={profile}
      showIdentityName={false}
      showAvatar={false}
      skillsTitle="Skills (self-reported)"
      skillsDescription="Job-mapped skills are on the Analysis tab. This list is what they reported on their profile."
      className="pt-6 pb-6"
    />
  )

  const pdfActions =
    isProfilePdfEnabled() && state.profileId ? (
      <PageHeaderActions secondary={<ProfilePdfDownloadButton profileId={state.profileId} />} />
    ) : undefined

  return (
    <MatchDetailShell
      mode="person"
      title={state.displayName}
      description={state.description}
      breadcrumbs={state.breadcrumbs}
      actions={pdfActions}
      activeTab={state.activeTab}
      onTabChange={state.setActiveTab}
      hasAnalysis={state.hasAnalysis}
      analysisUnavailableRetry={analysisRetry}
      hasCoverLetter={state.hasCoverLetter}
      analysisPanel={
        state.evaluationId ? (
          <EvaluationAnalysisView
            evaluationId={state.evaluationId}
            retryAction={analysisRetry}
          />
        ) : null
      }
      letterPanel={
        state.applicant ? (
          <ApplicationCoverLetterView
            applicationId={state.applicant.id}
            candidateName={state.displayName}
          />
        ) : null
      }
      secondaryPanel={profilePanel}
    />
  )
}
